/**
 * session — the SPA's vocabulary for WHO the caller is, kept pure.
 *
 * The daemon authorizes every route except `GET /health` at its router head, and
 * a human principal is asserted by the oauth2-proxy session cookie in front of
 * it (`infra/oauth2-proxy`). That makes exactly two identity failures possible,
 * and they are NOT interchangeable:
 *
 *   - `401 {"error":"missing_token"|"invalid_token"}` — no usable credential.
 *     The answer is to sign in.
 *   - `403 {"error":"principal_not_permitted"}` — the credential is good and the
 *     sign-in ALREADY succeeded; the account's email domain is simply outside
 *     the allowlist. Bouncing this caller back to the proxy re-authenticates the
 *     same rejected identity and lands on the same 403 — an infinite loop shown
 *     to precisely the users who can never succeed. So THAT 403 is terminal:
 *     it gets a readable screen naming the two permitted domains, never a
 *     redirect.
 *
 * **Not every 403 is the daemon's.** The proxy in front of it emits one too, and
 * conflating them is why this classifier reads the body instead of the status
 * alone. Under the htpasswd bridge (`--skip-provider-button=false`) an
 * unauthenticated non-Ajax request is answered with the sign-in FORM, rendered
 * inline as `403` HTML — not a redirect. Classified on status alone that is
 * indistinguishable from the daemon's denial, so a first visit or an expired
 * cookie — the ordinary signed-out path — would render the terminal "this
 * account is refused" screen at someone whose account is perfectly fine, with no
 * way back to sign-in. So a 403 is terminal ONLY when it carries the daemon's
 * own `{"error":"principal_not_permitted"}` body; anything else is `signed-out`.
 *
 * That asymmetry is deliberate, and it is the safe direction: misreading a
 * genuine daemon 403 as signed-out sends someone to a sign-in they cannot pass —
 * self-evident and recoverable. Misreading the proxy's 403 as terminal strands
 * them with a false accusation. An unparseable or empty body therefore falls to
 * `signed-out`.
 *
 * **This is flag-independent by design.** It keys on the daemon's error code,
 * not on `--skip-provider-button`, so the Google cutover back to
 * `--skip-provider-button=true` (which restores the `302`-to-Google bounce for
 * unauthenticated requests) does NOT require revisiting this logic. An earlier
 * version of this comment asserted that 302 as the only signed-out signal; the
 * bridge falsified it, and pinning behaviour to a proxy flag value is the defect
 * that cost. Redirects stay classified as `signed-out` for exactly that reason —
 * a cross-origin redirect is not readable by `fetch`, so {@link
 * classifySessionFailure} folds any redirect into `signed-out`; see
 * `api-fetch.ts` for why the requests are issued with `redirect: 'manual'`.
 *
 * This module is pure — no fetch, no DOM, no storage — so every transition is
 * unit-tested directly. The React shell lives in `SessionGate.tsx` and the
 * transport in `api-fetch.ts`.
 */

/** Why a call failed on identity: needs a sign-in, or is authenticated but barred. */
export type SessionFailureKind = 'signed-out' | 'not-permitted'

/**
 * The email domains the read plane admits. Duplicated deliberately: the single
 * enforcement point is the edge + daemon allowlist
 * (`--email-domain` in `infra/oauth2-proxy/oauth2-proxy.service` and the
 * daemon's own check), and this copy exists ONLY so the not-permitted screen can
 * tell an operator which account to use instead of leaving them guessing. It
 * grants nothing. If the enforced list changes, this list is cosmetic drift —
 * never the other way round.
 */
export const ALLOWED_EMAIL_DOMAINS: readonly string[] = ['obvious.ai', 'flatfile.io']

/** oauth2-proxy's sign-in entry point (`--proxy-prefix=/oauth2`, same origin as the SPA). */
export const SIGN_IN_PATH = '/oauth2/start'

/**
 * How long a sign-in attempt suppresses the next automatic one. If the redirect
 * worked we are gone from this page and the window never matters; if we are
 * still here and still signed out after a round trip, the session is not
 * establishing (a dropped cookie over plain HTTP is the documented cause) and
 * re-firing the redirect would spin. Suppression converts that spin into a
 * visible, clickable signed-out state.
 */
export const SIGN_IN_RETRY_WINDOW_MS = 30_000

/** Storage key holding the epoch-ms of the last automatic sign-in redirect. */
export const SIGN_IN_ATTEMPT_KEY = 'evaluator.signInAttemptAt'

/**
 * The statuses that mean "go somewhere else" — the sign-in bounce, when a stub
 * or a non-browser fetch lets us read it numerically. Deliberately an explicit
 * set rather than the `300..399` range: `304 Not Modified` also lives in that
 * range and is load-bearing for this SPA, whose polling is built on
 * `If-None-Match`. Treating the range as redirects turned every idle poll into
 * a spurious sign-out.
 */
const REDIRECT_STATUSES: ReadonlySet<number> = new Set([301, 302, 303, 307, 308])

/**
 * The daemon's own code for "authenticated, but this identity is barred" — the
 * ONE body that makes a 403 terminal. Matched exactly, rather than sniffing the
 * proxy's HTML for a form, so that a body we cannot read or did not expect
 * degrades to the recoverable `signed-out` branch instead of the terminal one.
 */
export const DAEMON_NOT_PERMITTED_CODE = 'principal_not_permitted'

/**
 * Could this response be an identity failure at all? Callers use this to decide
 * whether reading the body is warranted — the body is a one-shot resource, and
 * consuming it on a `304` or a `404` would corrupt the caller's own read.
 */
export function isPossibleIdentityFailure(status: number, type?: string): boolean {
  return type === 'opaqueredirect' || REDIRECT_STATUSES.has(status) || status === 401 || status === 403
}

/**
 * Classify one response as an identity failure, or null when identity is not
 * the problem. `type` is the `Response.type` — an `opaqueredirect` is the edge
 * answering "go authenticate" in a form the browser refuses to let us read.
 * `daemonErrorCode` is the `error` field of the response body when one could be
 * parsed (see {@link DAEMON_NOT_PERMITTED_CODE}); pass `null` when the body was
 * absent, empty, or unparseable. It is only consulted for a `403`, which is the
 * one status two different servers on this path can produce.
 */
export function classifySessionFailure(
  status: number,
  type?: string,
  daemonErrorCode?: string | null
): SessionFailureKind | null {
  if (type === 'opaqueredirect' || REDIRECT_STATUSES.has(status)) {
    return 'signed-out'
  }
  if (status === 401) {
    return 'signed-out'
  }
  if (status === 403) {
    // Terminal only on the daemon's explicit denial. The proxy's inline sign-in
    // form — and any body we could not read — means "authenticate", not "you are
    // barred". See the module header for why this errs toward recoverable.
    return daemonErrorCode === DAEMON_NOT_PERMITTED_CODE ? 'not-permitted' : 'signed-out'
  }
  return null
}

/**
 * Fold two observed failures into the one the UI should show. Feeds poll
 * independently, so a stale `signed-out` from one endpoint can race a
 * `not-permitted` from another. `not-permitted` wins: it is the more specific
 * fact (the sign-in demonstrably worked) and it is terminal, so it must never be
 * downgraded into a redirect.
 */
export function mergeSessionFailure(
  a: SessionFailureKind | null,
  b: SessionFailureKind | null
): SessionFailureKind | null {
  if (a === 'not-permitted' || b === 'not-permitted') {
    return 'not-permitted'
  }
  return a ?? b
}

/**
 * Build the proxy sign-in URL that returns to `href` afterwards. `rd` is passed
 * as a root-relative path (never an absolute URL): oauth2-proxy validates the
 * redirect target and rejects off-host destinations, and keeping it relative
 * means there is no open-redirect surface to get wrong here.
 */
export function signInUrl(href: string): string {
  const rd = relativeTarget(href)
  return `${SIGN_IN_PATH}?rd=${encodeURIComponent(rd)}`
}

/**
 * Reduce a full href to its path + query + hash, defaulting to `/`. Anything
 * unparseable degrades to `/` rather than throwing — a sign-in must not be
 * blocked by a weird location.
 */
function relativeTarget(href: string): string {
  try {
    const url = new URL(href, 'http://localhost')
    const target = `${url.pathname}${url.search}${url.hash}`
    return target.startsWith('/') ? target : '/'
  } catch {
    return '/'
  }
}

/**
 * Decide what to do about a `signed-out` state: bounce to the proxy now, or stop
 * bouncing and let the operator choose. `manual` is returned when a redirect was
 * already attempted inside {@link SIGN_IN_RETRY_WINDOW_MS} — that is the
 * loop-breaker.
 */
export function decideSignIn(now: number, lastAttemptAt: number | null): 'redirect' | 'manual' {
  if (lastAttemptAt === null) {
    return 'redirect'
  }
  // A clock that moved backwards (system time change, restored tab) would make
  // the elapsed check negative and suppress forever; treat it as "long ago".
  const elapsed = now - lastAttemptAt
  if (elapsed < 0 || elapsed >= SIGN_IN_RETRY_WINDOW_MS) {
    return 'redirect'
  }
  return 'manual'
}
